import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { salesOrderService } from '../../services/salesOrderService';
import type { SalesOrder } from '../../types/salesOrder';
import { useLanguage } from '../../features/language/useLanguage';

interface SalesOrderMiniTableProps {
  customerId: string;
}

export const SalesOrderMiniTable: React.FC<SalesOrderMiniTableProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    salesOrderService.getAll()
      // Filter by the customer ID stored in the 'customer' field (maps to customer_id on backend).
      .then((all) => setOrders(all.filter((so) => so.customer === customerId)))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  if (loading) {
    return <div className="p-4 text-sm text-gray-500 italic">{t('selling.customerList.loading')}</div>;
  }

  if (orders.length === 0) {
    return <div className="p-4 text-sm text-gray-500 italic">{t('salesOrder.list.empty')}</div>;
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 m-2">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('salesOrder.list.title')}</h4>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('forms.common.orderNumber')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('reports.general.table.date')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('forms.common.status')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">{t('reports.general.table.amount')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-500">{t('salesOrder.list.expectedShipDate')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-500">{t('settingsMcp.table.actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {orders.map((so) => (
            <tr key={so.id}>
              <td className="px-4 py-2 font-medium text-gray-900">{so.orderNumber}</td>
              <td className="px-4 py-2 text-gray-500">{new Date(so.orderDate).toLocaleDateString()}</td>
              <td className="px-4 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                  ${so.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                    so.status === 'Sent' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                  {t(`salesOrder.list.status.${so.status.toLowerCase()}`, undefined, so.status)}
                </span>
              </td>
              <td className="px-4 py-2 text-right text-gray-900">${so.totalAmount.toFixed(2)}</td>
              <td className="px-4 py-2 text-gray-500">
                {so.expectedShipDate ? new Date(so.expectedShipDate).toLocaleDateString() : t('settingsMcp.values.none')}
              </td>
              <td className="px-4 py-2 text-right">
                <Link to={`/sales-orders/${so.id}`} className="text-primary-600 hover:text-primary-800">
                  {t('reports.shift.view')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
